import {css, StyleSheet} from 'aphrodite';
import React, {useReducer, Dispatch, useEffect} from 'react';

import {assert} from 'Asserts';
import makeCancelable from 'lib/makeCancelable';
import ConfirmPropagationPane from 'molbio/biointelligent-links/replace-link/ConfirmPropagationPane';
import {
  ReplaceLinkStepKey,
  ORDERED_STEPS,
  getInitialReducerstate,
  DiffDetails,
  ReplaceableBiointelligentLinkType,
  ReplaceLinkModalReducerState,
  LINK_TYPE_TO_COMPONENT_POLYMER_SUBTYPE,
} from 'molbio/biointelligent-links/replace-link/constants';
import ContainerSearchPane from 'molbio/biointelligent-links/replace-link/ContainerSearchPane';
import reduce, {ReplaceLinkAction} from 'molbio/biointelligent-links/replace-link/replaceLinkReducer';
import ReviewEditsPane from 'molbio/biointelligent-links/replace-link/ReviewEditsPane';
import {diffSequences, replaceBiointelligentLinks} from 'molbio/dao/SequenceDAO';
import notifications from 'notifications';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import StepNav, {StepNavSize, StepNavVariant} from 'ui-library/StepNav';
import APIUtil from 'util/APIUtil';

export interface ReplaceBiointelligentLinksModalProps {
  existingLinkPolymerId: string;
  newLinkPolymerId: string;
  onRequestClose: () => void;
  linkType: ReplaceableBiointelligentLinkType;
}

function getButtons(
  state: ReplaceLinkModalReducerState,
  dispatch: Dispatch<ReplaceLinkAction>,
  existingLinkPolymerId: string,
  newLinkPolymerId: string,
  linkType: ReplaceableBiointelligentLinkType,
  onRequestClose: () => void
): Array<ModalButton> {
  const buttons: Array<ModalButton> = [];
  const currentStepIndex = ORDERED_STEPS.indexOf(state.currentStepKey);
  if (currentStepIndex === 0) {
    buttons.push({text: 'Cancel', cancel: true});
  } else {
    buttons.push({
      text: 'Cancel',
      cancel: true,
      pullLeft: true,
      asLink: true,
      dataTestAttrs: {
        component: 'ReplaceBiointelligentLinksModal',
        key: ModalButtonKey.CANCEL,
      },
    });
    buttons.push({
      text: 'Back',
      onClick: () => {
        dispatch({type: 'UPDATE_CURRENT_STEP', value: ORDERED_STEPS[currentStepIndex - 1]});
      },
      dataTestAttrs: {
        component: 'ReplaceBiointelligentLinksModal',
        key: ModalButtonKey.BACK,
      },
    });
  }

  if (currentStepIndex !== ORDERED_STEPS.length - 1) {
    let nextButtonDisabled: boolean;
    if (currentStepIndex === 0) {
      nextButtonDisabled = state.diffDetails == null;
    } else {
      nextButtonDisabled = state.selectedContainerPolymerIds.length === 0;
    }

    buttons.push({
      text: 'Next',
      onClick: () => {
        dispatch({type: 'UPDATE_CURRENT_STEP', value: ORDERED_STEPS[currentStepIndex + 1]});
      },
      submit: true,
      disabled: nextButtonDisabled,
      dataTestAttrs: {
        component: 'ReplaceBiointelligentLinksModal',
        key: ModalButtonKey.NEXT,
      },
    });
  } else {
    buttons.push({
      text: 'Replace link',
      onClick: async () => {
        await replaceBiointelligentLinks({
          originalComponentId: existingLinkPolymerId,
          newComponentId: newLinkPolymerId,
          biointelligentLinkType: linkType,
          containerIds: state.selectedContainerPolymerIds,
          editOps: state.diffDetails!.ops,
          componentPolymerSubtype: LINK_TYPE_TO_COMPONENT_POLYMER_SUBTYPE[linkType],
        });
        notifications.success(
          'Link replacement initiated. You will receive an email when the operation completes.'
        );
        onRequestClose();
      },
      submit: true,
      disabled: !state.diffDetails!.ops || state.selectedContainerPolymerIds.length === 0,
      dataTestAttrs: {
        component: 'ReplaceBiointelligentLinksModal',
        key: ModalButtonKey.SUBMIT,
      },
    });
  }

  return buttons;
}

export default function ReplaceBiointelligentLinksModal({
  existingLinkPolymerId,
  newLinkPolymerId,
  linkType,
  onRequestClose,
}: ReplaceBiointelligentLinksModalProps): JSX.Element {
  const [reducerState, dispatch] = useReducer(reduce, getInitialReducerstate());

  useEffect(() => {
    dispatch({type: 'SET_DIFF_DETAILS', value: null});

    let diffFunction: (polymerAId: string, polymerBId: string) => Promise<DiffDetails>;
    if (
      APIUtil.isNucleotideSequence(existingLinkPolymerId) &&
      APIUtil.isNucleotideSequence(newLinkPolymerId)
    ) {
      diffFunction = diffSequences;
    } else if (APIUtil.isProtein(existingLinkPolymerId) && APIUtil.isProtein(newLinkPolymerId)) {
      assert(false, 'Proteins are not currently supported');
    } else {
      assert(false, 'Unexpectedly received mismatched or non-polymer IDs');
    }
    const {promise, cancel} = makeCancelable(diffFunction(existingLinkPolymerId, newLinkPolymerId));

    async function updateDiffDetails(): Promise<void> {
      try {
        const diffResult = await promise;
        dispatch({type: 'SET_DIFF_DETAILS', value: diffResult});
      } catch (e) {
        if (!e.isCanceled) {
          throw e;
        }
      }
    }

    updateDiffDetails();

    return () => {
      cancel();
    };
  }, [existingLinkPolymerId, newLinkPolymerId, dispatch]);

  return (
    <Modal
      title="Replace link"
      onRequestClose={onRequestClose}
      buttons={getButtons(
        reducerState,
        dispatch,
        existingLinkPolymerId,
        newLinkPolymerId,
        linkType,
        onRequestClose
      )}
      fullWidth={true}
      fullHeight={true}
      scrollable={false}
      bodyPadded={false}
    >
      <div className={css(styles.container)}>
        <div className={css(styles.steps)}>
          <StepNav<ReplaceLinkStepKey>
            size={StepNavSize.LARGE}
            variant={StepNavVariant.FLAT}
            steps={[
              {
                key: ReplaceLinkStepKey.REVIEW_EDITS,
                title: 'Review edits',
              },
              {
                key: ReplaceLinkStepKey.SELECT_SEQUENCES,
                title: 'Select sequences to update',
              },
              {
                key: ReplaceLinkStepKey.CONFIRM,
                title: 'Confirm and finish',
              },
            ]}
            statusByStep={reducerState.statusByStep}
            currentStepKey={reducerState.currentStepKey}
            onUpdateCurrentStep={(stepKey: ReplaceLinkStepKey) => {
              dispatch({type: 'UPDATE_CURRENT_STEP', value: stepKey});
            }}
          />
        </div>
        <hr className={css(styles.horizontalRule)} />
        <div className={css(styles.pane)}>
          {reducerState.currentStepKey === ReplaceLinkStepKey.REVIEW_EDITS && (
            <ReviewEditsPane
              referenceSequenceId={existingLinkPolymerId}
              diffDetails={reducerState.diffDetails}
              sidebarExpanded={reducerState.sidebarExpanded}
              onToggleSidebarExpanded={() => {
                dispatch({type: 'TOGGLE_SIDEBAR_EXPANDED'});
              }}
            />
          )}
          {reducerState.currentStepKey === ReplaceLinkStepKey.SELECT_SEQUENCES && (
            <ContainerSearchPane
              existingLinkPolymerId={existingLinkPolymerId}
              newLinkPolymerId={newLinkPolymerId}
              linkType={linkType}
              selectedIds={reducerState.selectedContainerPolymerIds}
              setSelectedIds={(selectedIds: Array<string>) => {
                dispatch({type: 'SET_SELECTED_CONTAINER_POLYMER_IDS', value: selectedIds});
              }}
              sidebarExpanded={reducerState.sidebarExpanded}
              onToggleSidebarExpanded={() => {
                dispatch({type: 'TOGGLE_SIDEBAR_EXPANDED'});
              }}
            />
          )}
          {reducerState.currentStepKey === ReplaceLinkStepKey.CONFIRM && (
            <ConfirmPropagationPane
              sidebarExpanded={reducerState.sidebarExpanded}
              onToggleSidebarExpanded={() => {
                dispatch({type: 'TOGGLE_SIDEBAR_EXPANDED'});
              }}
            />
          )}
        </div>
      </div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    width: '100%',
    height: '100%',
    flexDirection: 'column',
  },
  pane: {
    flex: 1,
  },
  horizontalRule: {margin: '0px'},
  steps: {margin: '8px 16px'},
});
